import React from 'react';
import { motion } from 'framer-motion';
import { FaSpinner } from 'react-icons/fa';

type AnimationStyle = 'dots' | 'pulse' | 'typing' | 'spinner';

interface Props {
  message?: any;
  style?: AnimationStyle;
  color?: string;
}

const SavingIndicator: React.FC<Props> = ({
  message = "Saving...",
  style = 'dots',
  color = '#0d6efd' // Bootstrap blue
}) => {
  const dotBounce = {
    animate: {
      y: [0, -6, 0],
      transition: { duration: 0.6, repeat: Infinity, ease: 'easeInOut' }
    }
  };

  const renderContent = () => {
    switch (style) {
      case 'dots':
        return (
          <div className="d-flex align-items-center gap-2 text-muted-theme">
            <div className="d-flex gap-1 align-items-center" style={{ height: '1rem' }}>
              {[0, 1, 2].map(i => (
                <motion.span
                  key={i}
                  className="rounded-circle"
                  style={{
                    width: 6, height: 6,
                    backgroundColor: color,
                    display: 'inline-block'
                  }}
                  variants={dotBounce}
                  animate="animate"
                  transition={{ delay: i * 0.2 }}
                />
              ))}
            </div>
            <span className="fw-medium">{message}</span>
          </div>
        );

      case 'pulse':
        return (
          <div className="d-flex align-items-center gap-2 text-muted-theme">
            <div className="position-relative" style={{ width: 20, height: 20 }}>
              <motion.div
                className="position-absolute top-0 start-0 w-100 h-100 rounded-circle"
                initial={{ scale: 1, opacity: 0.5 }}
                animate={{ scale: 2, opacity: 0 }}
                transition={{ duration: 1.5, repeat: Infinity, ease: 'easeOut' }}
                style={{ backgroundColor: color }}
              />
              <div style={{
                position: 'relative',
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: color,
                margin: 'auto',
                top: 4
              }} />
            </div>
            <span className="fw-medium">{message}</span>
          </div>
        );

      case 'typing':
        return (
          <div className="d-flex align-items-center gap-2 text-muted-theme">
            <span className="fw-medium">{message}</span>
            <span className="dot-typing" />
          </div>
        );

      case 'spinner':
      default:
        return (
          <div className="d-flex align-items-center gap-2 text-muted-theme">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
              style={{
                fontSize: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color
              }}
            >
              <FaSpinner />
            </motion.div>
            <span className="fw-medium">{message}</span>
          </div>
        );
    }
  };

  return <div className="px-2 py-1">{renderContent()}</div>;
};

export default SavingIndicator;

<style>
  {
    `
    .dot-typing::after {
  content: '';
  display: inline-block;
  width: 1em;
  text-align: left;
  animation: dotty 1.5s steps(3, end) infinite;
}

@keyframes dotty {
  0%   { content: ''; }
  33%  { content: '.'; }
  66%  { content: '..'; }
  100% { content: '...'; }
}
    `
  }
</style>